import { Injectable, inject } from '@angular/core';
import { CookieService } from './cookie.service';

export interface UserSettings {
  greenHighlightDuration: number; // seconds
  redHighlightDuration: number;   // seconds
  defaultSortField: string;       // default sort field for tasks
  defaultSortDirection: 'asc' | 'desc'; // default sort direction
  myTasksFilterEnabled: boolean;  // global filter to show only assigned tasks
  theme: 'light' | 'dark' | 'system'; // user's preferred theme
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private readonly cookieService = inject(CookieService);
  private readonly SETTINGS_COOKIE_KEY = 'dbweb_user_settings';
  
  private readonly defaultSettings: UserSettings = {
    greenHighlightDuration: 5, // 5 seconds for new task green highlight
    redHighlightDuration: 5,   // 5 seconds for task removal red highlight
    defaultSortField: 'docnumber',    // default to Asset No sorting
    defaultSortDirection: 'asc',       // default to ascending order
    myTasksFilterEnabled: false,      // unchecked by default
    theme: 'light'                    // default to light theme
  };

  getSettings(): UserSettings {
    const savedSettings = this.cookieService.getJsonCookie<UserSettings>(this.SETTINGS_COOKIE_KEY);
    
    if (!savedSettings) {
      return { ...this.defaultSettings };
    }
    
    // Merge with defaults to handle missing properties in saved settings
    return {
      greenHighlightDuration: savedSettings.greenHighlightDuration ?? this.defaultSettings.greenHighlightDuration,
      redHighlightDuration: savedSettings.redHighlightDuration ?? this.defaultSettings.redHighlightDuration,
      defaultSortField: savedSettings.defaultSortField ?? this.defaultSettings.defaultSortField,
      defaultSortDirection: savedSettings.defaultSortDirection ?? this.defaultSettings.defaultSortDirection,
      myTasksFilterEnabled: savedSettings.myTasksFilterEnabled ?? this.defaultSettings.myTasksFilterEnabled,
      theme: savedSettings.theme ?? this.defaultSettings.theme
    };
  }

  saveSettings(settings: UserSettings): void {
    // Validate settings before saving
    const validSortFields = ['docnumber', 'lastTouched', 'recordkey', 'duedate', 'consigner', 'make', 'assignmentStatus', 'destinationfilter'];
    
    const validatedSettings: UserSettings = {
      greenHighlightDuration: Math.max(1, Math.min(360, settings.greenHighlightDuration)), // 1-360 seconds
      redHighlightDuration: Math.max(1, Math.min(360, settings.redHighlightDuration)),     // 1-360 seconds
      defaultSortField: validSortFields.includes(settings.defaultSortField) ? settings.defaultSortField : 'docnumber',
      defaultSortDirection: ['asc', 'desc'].includes(settings.defaultSortDirection) ? settings.defaultSortDirection : 'asc',
      myTasksFilterEnabled: Boolean(settings.myTasksFilterEnabled), // ensure boolean type
      theme: ['light', 'dark', 'system'].includes(settings.theme) ? settings.theme : 'light' // validate theme
    };
    
    this.cookieService.setJsonCookie(this.SETTINGS_COOKIE_KEY, validatedSettings);
  }

  resetToDefaults(): UserSettings {
    this.cookieService.deleteCookie(this.SETTINGS_COOKIE_KEY);
    return { ...this.defaultSettings };
  }
}